import React from 'react';
import { C } from '../styles/theme';
import logo from '../assets/startup/static-logo.png';

// The loading gate shown while AuthContext resolves the session.
//
// Deliberately identical to the #pv-splash block in public/index.html. That
// splash paints before React exists; this takes over the moment React mounts.
// Because the two are pixel-matched the handoff is invisible — which is the
// whole point, since two different screens is what made one load look like
// several. Change one, change both.
//
// The canvas is solid, not a blurred scrim: this gate covers a page that has
// not rendered yet, so there is nothing behind it to blur, and a dark scrim
// over nothing just reads as a dark screen.
//
// The logo sits in a white circle emitting concentric rings — a radar pulse.
// Three rings share one animation, each offset by a third of its duration,
// which is what makes the emission continuous rather than a repeating single
// throb. The disc breathes on the same cycle so the pulse looks like it is
// coming from the logo instead of happening around it.
//
// Motion is skipped for anyone who has asked their system to reduce it: the
// rings do not render and the disc sits still. Nothing else changes, so the
// screen still reads correctly.
const LoadingScreen = () => {
  // If the startup splash from public/index.html is still on screen, render
  // nothing at all.
  //
  // That splash removes itself the moment #root gets children. Returning null
  // keeps #root empty, so the splash stays up and continues its own animation
  // until the real page is ready to paint. One screen, uninterrupted.
  //
  // Rendering a second identical screen here is what produced the double load:
  // the splash would tear down mid-cycle and this would mount and restart from
  // frame zero, which reads as two loads no matter how well the two match.
  //
  // Once the splash is gone this behaves normally, so later gates — a session
  // refresh, a slow permission read — still show something rather than a blank
  // page.
  if (typeof document !== 'undefined' && document.getElementById('pv-splash')) {
    return null;
  }

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const FIELD = 150;  // room for the rings at full expansion
  const DISC = 88;    // white circle holding the logo

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Matches the #pv-splash scrim in public/index.html: translucent over
        // whatever is behind, so a gate that fires mid-session dims the page
        // instead of blanking it. On a cold start there is nothing behind and
        // it reads as the plain canvas.
        background: 'rgba(244,247,245,0.22)',
        backdropFilter: 'blur(10px) saturate(115%)',
        WebkitBackdropFilter: 'blur(10px) saturate(115%)',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: FIELD,
          height: FIELD,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {/* Rings — behind the disc, expanding past its edge. */}
        {!reduceMotion &&
          [0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                position: 'absolute',
                width: DISC,
                height: DISC,
                borderRadius: '50%',
                border: `2px solid ${C.primary}`,
                opacity: 0,
                animation: 'pv-ripple 2.4s cubic-bezier(0.22, 0.61, 0.36, 1) infinite',
                animationDelay: `${i * 0.8}s`,
              }}
            />
          ))}

        <div
          style={{
            position: 'relative',
            width: DISC,
            height: DISC,
            borderRadius: '50%',
            background: '#FFFFFF',
            display: 'grid',
            placeItems: 'center',
            boxShadow: '0 6px 22px rgba(11,18,32,0.16)',
            animation: reduceMotion ? 'none' : 'pv-breathe 2.4s ease-in-out infinite',
          }}
        >
          <img
            src={logo}
            alt="Pharvlink"
            style={{ width: 46, height: 46, objectFit: 'contain', display: 'block' }}
          />
        </div>
      </div>

      <p
        style={{
          margin: '24px 0 0',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: C.ink,
        }}
      >
        Pharvlink
      </p>

      <style>{`
        @keyframes pv-ripple {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { opacity: 0.06; }
          100% { transform: scale(1.7); opacity: 0; }
        }
        @keyframes pv-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.045); }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;